import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CircleAlert, CircleCheck, CircleX, FileText, Loader2 } from "lucide-react";
import AuthService from "../../auth/Authorization.service";
import { useProfileStore } from "../../../core/store/useProfileStore";
import PublicSampleService from "../../../sdk/services/PublicSampleService";
import LandingSampleDialog from "./LandingSampleDialog";
import { SAMPLE_EXAM_ID, track } from "./landingTrack";
import {
  INITIAL_SAMPLE_CHECK,
  selectSampleChoice,
  beginSampleCheck,
  applySampleResult,
  applySampleFailure,
  retrySampleCheck,
  canRespond,
  choicesAreLocked,
  sampleResultCopy,
  choiceOutcomeLabel,
  classifySampleFailure,
  type SampleAnswerView,
} from "./sampleAnswerFlow";
import {
  SAMPLE_QUESTION_CHOICES,
  SAMPLE_QUESTION_META,
  SAMPLE_QUESTION_STATEMENT,
} from "./sampleQuestion";
import {
  SAMPLE_CONTINUE_LABEL,
  SAMPLE_SIGN_UP_LABEL,
  resolveSampleVisitor,
  sampleDialogShouldAutoOpen,
  sampleKeepsResponder,
  sampleSectionCta,
  sampleSignUpOpensDialog,
} from "./sampleResultRouting";
import "./LandingSample.css";

/**
 * At least half of the sample section is inside the viewport.
 * The ratio is the visible fraction of the section, not of the screen.
 */
function sampleSectionHalfVisible(entry: IntersectionObserverEntry): boolean {
  return entry.isIntersecting && entry.intersectionRatio >= 0.5;
}

export default function LandingSample() {
  const [view, setView] = useState<SampleAnswerView>(INITIAL_SAMPLE_CHECK);
  const [clock, setClock] = useState(() => Date.now());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogDismissed, setDialogDismissed] = useState(false);
  // Disabled only updates after render, so the ref blocks a second post.
  const requestLock = useRef(false);
  // Cleanup marks a late response so it cannot setState.
  const cancelled = useRef(false);
  const criarContaRef = useRef<HTMLButtonElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  // StrictMode re-runs the effect. Leave this set so the second run does not record again.
  const sampleViewed = useRef(false);
  const token = AuthService.getAccessToken();
  const profile = useProfileStore((state) => state.profile);
  const status = useProfileStore((state) => state.status);
  const load = useProfileStore((state) => state.load);
  const visitor = resolveSampleVisitor({
    token,
    profileStatus: status,
    profile,
  });
  const cta = sampleSectionCta({ visitor, phase: view.phase });

  useEffect(() => {
    cancelled.current = false;
    return () => {
      cancelled.current = true;
    };
  }, []);

  useEffect(() => {
    if (sampleViewed.current) return;
    const node = sectionRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (sampleViewed.current) return;
        const entry = entries[entries.length - 1];
        if (!entry || !sampleSectionHalfVisible(entry)) return;
        sampleViewed.current = true;
        track("sample_viewed");
        observer.disconnect();
      },
      { root: null, threshold: 0.5 },
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, []);

  // One timeout for a 429 cooldown. Not a poll.
  useEffect(() => {
    const until = view.respondLockedUntil;
    if (until == null) return;
    const remaining = until - Date.now();
    if (remaining <= 0) {
      setClock((tick) => (tick >= until ? tick : until));
      return;
    }
    const timer = window.setTimeout(() => {
      setClock((tick) => Math.max(tick, until, Date.now()));
    }, remaining);
    return () => window.clearTimeout(timer);
  }, [view.respondLockedUntil]);

  useEffect(() => {
    if (token && status === "idle") load();
  }, [token, status, load]);

  // Focus Criar conta before the dialog mounts so its restore returns there.
  useLayoutEffect(() => {
    if (
      !sampleDialogShouldAutoOpen({
        visitor,
        phase: view.phase,
        dismissed: dialogDismissed,
        open: dialogOpen,
      })
    ) {
      return;
    }
    criarContaRef.current?.focus({ preventScroll: true });
    setDialogOpen(true);
  }, [visitor, view.phase, dialogDismissed, dialogOpen]);

  function closeSampleDialog() {
    setDialogDismissed(true);
    setDialogOpen(false);
  }

  function handleAnswer() {
    if (requestLock.current) return;
    const choice = view.selectedId;
    const next = beginSampleCheck(view, Date.now());
    if (!next || !choice) return;
    requestLock.current = true;
    setView(next);
    PublicSampleService.checkAnswer(choice)
      .then((body) => {
        if (cancelled.current) return;
        if (typeof body?.correct !== "boolean") {
          setView((current) => applySampleFailure(current, "error", Date.now()));
          track("sample_check_failed", { reason: "error" });
        } else {
          setView((current) => applySampleResult(current, body.correct));
          track("sample_question_answered", {
            exam: SAMPLE_EXAM_ID,
            correct: body.correct,
          });
        }
      })
      .catch((error: unknown) => {
        if (cancelled.current) return;
        const reason = classifySampleFailure(error);
        setView((current) => applySampleFailure(current, reason, Date.now()));
        track("sample_check_failed", { reason });
      })
      .finally(() => {
        requestLock.current = false;
      });
  }

  const copy = sampleResultCopy(view);

  return (
    <section id="amostra" ref={sectionRef} aria-labelledby="amostra-heading" className="lsamp">
      <div className="lsamp__header">
        <p className="lsamp__eyebrow">AMOSTRA GRÁTIS</p>
        <h2 id="amostra-heading" tabIndex={-1} className="lsamp__title">
          Experimente antes de criar conta
        </h2>
        <div className="lsamp__lead">
          <p>Responda e veja se acertou, sem criar conta. Sua resposta não é salva.</p>
          <p>Para resolver mais questões e fazer simulados, crie sua conta.</p>
        </div>
      </div>
      <p className="lsamp__count">
        <FileText className="lsamp__count-icon" aria-hidden="true" size={16} />
        <span className="lsamp__count-chip">1 questão de exemplo · sem cadastro</span>
      </p>
      <div className="lsamp__panel">
        <div className="lsamp__orb" aria-hidden="true" />
        <div className="lsamp__spark" aria-hidden="true" />
        <div className="lsamp__card">
          <p className="lsamp__meta">{SAMPLE_QUESTION_META}</p>
          <p className="lsamp__statement">{SAMPLE_QUESTION_STATEMENT}</p>
          <div
            className="lsamp__choices"
            role="radiogroup"
            aria-labelledby="amostra-choice-prompt"
          >
            <p id="amostra-choice-prompt" className="lsamp__prompt">
              Escolha uma alternativa
            </p>
            {SAMPLE_QUESTION_CHOICES.map((choice) => {
              const outcome = choiceOutcomeLabel(view, choice.id);
              return (
                <label
                  key={choice.id}
                  className={
                    outcome === "Resposta correta"
                      ? "lsamp__choice lsamp__choice--right"
                      : outcome === "Resposta incorreta"
                        ? "lsamp__choice lsamp__choice--wrong"
                        : "lsamp__choice"
                  }
                >
                  <input
                    className="lsamp__choice-input"
                    type="radio"
                    name="sample-question-choice"
                    value={choice.id}
                    checked={view.selectedId === choice.id}
                    disabled={choicesAreLocked(view)}
                    onChange={() =>
                      setView((current) => selectSampleChoice(current, choice.id))
                    }
                  />
                  <span className="lsamp__letter" aria-hidden="true">
                    {choice.id.toUpperCase()}
                  </span>
                  <span className="lsamp__choice-body">
                    <span className="lsamp__choice-text">{choice.text}</span>
                    {outcome ? <span className="lsamp__choice-label">{outcome}</span> : null}
                  </span>
                </label>
              );
            })}
          </div>
          <div
            className={copy ? `lsamp__result lsamp__result--${copy.tone}` : "lsamp__result"}
            aria-live="polite"
            aria-atomic="true"
          >
            {copy ? (
              <>
                <p className="lsamp__result-title">
                  {copy.tone === "right" ? (
                    <CircleCheck className="lsamp__result-icon" aria-hidden="true" size={22} />
                  ) : null}
                  {copy.tone === "wrong" ? (
                    <CircleX className="lsamp__result-icon" aria-hidden="true" size={22} />
                  ) : null}
                  {copy.tone === "failed" ? (
                    <CircleAlert className="lsamp__result-icon" aria-hidden="true" size={22} />
                  ) : null}
                  <span>{copy.title}</span>
                </p>
                <p className="lsamp__result-detail">{copy.detail}</p>
              </>
            ) : null}
          </div>
          <div className="lsamp__actions">
            {sampleKeepsResponder(view.phase) ? (
              <button
                type="button"
                className="lsamp__submit"
                disabled={!canRespond(view, clock)}
                onClick={handleAnswer}
              >
                {view.phase === "checking" ? (
                  <><Loader2 className="lsamp__spinner" aria-hidden="true" size={18} />{"Conferindo…"}</>
                ) : (
                  "Responder"
                )}
              </button>
            ) : null}
            {view.phase === "failed" ? (
              <button
                type="button"
                className="lsamp__retry"
                onClick={() => setView((current) => retrySampleCheck(current))}
              >
                Tentar de novo
              </button>
            ) : null}
            {cta.kind === "sign-up" ? (
              <button
                ref={criarContaRef}
                type="button"
                className={cta.primary ? "lsamp__submit" : "lsamp__signup"}
                onClick={() => {
                  track("sample_cta_clicked", { target: "registro" });
                  if (!sampleSignUpOpensDialog(view.phase)) return;
                  setDialogOpen(true);
                }}
              >
                {SAMPLE_SIGN_UP_LABEL}
              </button>
            ) : null}
            {cta.kind === "continue" ? (
              <Link
                className="lsamp__continue"
                to={cta.to}
                onClick={() => {
                  track("sample_cta_clicked", {
                    target: cta.to === "/home" ? "home" : "planos",
                  });
                }}
              >
                {SAMPLE_CONTINUE_LABEL}
              </Link>
            ) : null}
          </div>
        </div>
      </div>
      {dialogOpen &&
      visitor.status === "logged-out" &&
      view.answeredId !== null &&
      (view.phase === "right" || view.phase === "wrong") ? (
        <LandingSampleDialog
          open={dialogOpen}
          onClose={closeSampleDialog}
          correct={view.phase === "right"}
          pickedChoiceId={view.answeredId}
          meta={SAMPLE_QUESTION_META}
          statement={SAMPLE_QUESTION_STATEMENT}
          choices={SAMPLE_QUESTION_CHOICES}
        />
      ) : null}
    </section>
  );
}
